import Image from "next/image";
import { PortableText } from "next-sanity";
import { stegaClean } from "@sanity/client/stega";

import {
  PageHomeSchema,
  PageHomeWhyIqCardV2,
  PageHomeWhyIqCardV2Artwork,
  PageHomeWhyIqCardV2Tone,
} from "@/sanity/pages/PageHomeTemplate";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { cn } from "@/lib/utils";
import { DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";
import { WhyIqIconV2 } from "./WhyIqIconV2";

import temple from "@/public/home-v2/why-iq/backed-temple.jpg";
import dots from "@/public/home-v2/why-iq/dots-texture.jpg";
import statue from "@/public/home-v2/why-iq/risk-statue.webp";
import noise from "@/public/home-v2/why-iq/noise.webp";
import horse from "@/public/home-v2/why-iq/growth-horse.webp";
import fresco from "@/public/home-v2/why-iq/payouts-fresco.jpg";

// Figma: "IQ Capital | New UI 2026" node 5901-933 (desktop) / 6263-723 (mobile).
// Every size is Figma px / 8 → rem (1rem = 8px at the design width).

type CardPosition = "side" | "center-top" | "center-bottom";

type FloatingCardCopy = { title: string; text: string };

/** Product toasts on the Risk card, top to bottom. Figma DE 9646-2121 / BR 9573-986. */
const FLOATING_CARDS: Record<string, [FloatingCardCopy, FloatingCardCopy, FloatingCardCopy]> = {
  en: [
    { title: "$1 Challenge", text: "It’s the simplest way to explore IQ Capital and the IQC Trader platform" },
    { title: "Evaluation", text: "ideal if you already have the skills but prefer lower pricing and want to secure funding" },
    { title: "Instant Funding.", text: "You get immediate access to real capital — with pricing that reflects the fast-lane" },
  ],
  de: [
    { title: "1 $ Challenge", text: "Der einfachste Weg, IQ Capital und die IQC-Trader-Plattform kennenzulernen" },
    { title: "Evaluation", text: "Ideal, wenn du die Skills schon hast, einen günstigeren Preis willst und dir Funding sichern möchtest" },
    { title: "Instant Funding.", text: "Sofortiger Zugang zu echtem Kapital, mit einem Preis für die Überholspur" },
  ],
  br: [
    { title: "Challenge de US$ 1", text: "O jeito mais simples de conhecer a IQ Capital e a plataforma IQC Trader" },
    { title: "Avaliação", text: "Ideal se você já tem a habilidade, prefere um preço menor e quer garantir o seu funding" },
    { title: "Funding instantâneo.", text: "Acesso imediato a capital real, com um preço à altura do caminho mais rápido" },
  ],
};

const TONE_CLASS: Record<PageHomeWhyIqCardV2Tone, string> = {
  forest: "bg-[#123129]",
  sage: "bg-[#476757]",
  deep: "bg-[#031d16]",
  black: "bg-[#001a13]",
};

// ─── Decorative layers ───────────────────────────────────────────────────────

/** "image 418": dotted texture, rotated 90°, exclusion blend at 20%. */
function DotsTexture() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute top-0 left-0 flex h-[123.81rem] w-[120.29rem] items-center justify-center opacity-20 mix-blend-exclusion">
      <div className="relative h-[120.29rem] w-[123.81rem] shrink-0 rotate-90">
        <Image
          src={dots}
          alt=""
          fill
          sizes="60vw"
          className="object-cover"
        />
      </div>
    </div>
  );
}

/** Grain overlay: 853.5px tile at 3% opacity. */
function Noise({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      style={{ backgroundImage: `url(${noise.src})` }}
      className={cn(
        "pointer-events-none absolute h-[76.125rem] w-[96.5rem] bg-[length:106.69rem_106.69rem] bg-left-top bg-repeat opacity-[0.03]",
        className,
      )}
    />
  );
}

function IqLogoMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24.7136 23.9286"
      fill="white"
      aria-hidden
      className={cn("block shrink-0", className)}>
      <path d="M24.7136 18.2731C21.2187 18.2731 18.3745 15.4427 18.3745 11.9647H12.6918C12.6918 18.5616 18.0847 23.9284 24.7136 23.9284V18.2731Z" />
      <path d="M12.0211 0C5.39221 0 2.54215e-05 5.36612 2.54215e-05 11.963C2.54215e-05 18.5598 5.39221 23.9266 12.0211 23.9266V18.2714C8.52622 18.2714 5.68273 15.4416 5.68273 11.963C5.68273 8.48428 8.52622 5.65524 12.0211 5.65524C15.516 5.65524 18.3601 8.48499 18.3601 11.963H24.0429C24.0429 5.36612 18.65 0 12.0211 0Z" />
    </svg>
  );
}

/** One of the product "toast" cards floating over the Risk card. Decorative only. */
function FloatingProductCard({
  title,
  text,
  blurred,
  className,
}: {
  title: string;
  text: string;
  blurred?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "absolute flex items-center border-l-[#228e71]",
        blurred
          ? "h-[10.874rem] w-[43.583rem] gap-[3.099rem] border-l-[0.34rem] bg-white/[0.81] px-[3.552rem] py-[2.345rem] shadow-[0_0.633rem_4.179rem_rgba(0,0,0,0.09)] blur-[0.325rem]"
          : "h-[12.667rem] w-[50.768rem] gap-[3.61rem] border-l-[0.396rem] bg-white px-[4.137rem] py-[2.732rem] drop-shadow-[0_2.377rem_2.905rem_rgba(0,54,40,0.2)]",
        className,
      )}>
      <div
        className={cn(
          "grid shrink-0 place-items-center",
          blurred
            ? "h-[6.184rem] w-[6.184rem] rounded-[0.302rem] bg-[#123237] p-[0.868rem]"
            : "h-[7.203rem] w-[7.203rem] rounded-[0.451rem] bg-[#228e71] p-[1.298rem]",
        )}>
        <IqLogoMark className={blurred ? "h-[2.568rem] w-[2.652rem]" : "h-[2.991rem] w-[3.089rem]"} />
      </div>
      <div
        className={cn(
          "flex shrink-0 flex-col [word-break:break-word]",
          blurred ? "w-[27.197rem] gap-[0.394rem] text-[1.508rem]" : "w-[31.68rem] gap-[0.459rem] text-[1.757rem]",
        )}>
        <p className="mb-0 font-geist-mono leading-[normal] font-semibold text-black uppercase whitespace-nowrap">{title}</p>
        <p
          className={cn(
            "mb-0 font-geist leading-[1.3] text-black/40 opacity-80",
            blurred ? "tracking-[-0.0754rem]" : "tracking-[-0.0878rem]",
          )}>
          {text}
        </p>
      </div>
    </div>
  );
}

function CardArtwork({ kind, language }: { kind: PageHomeWhyIqCardV2Artwork; language: string }) {
  switch (kind) {
    case "temple":
      return (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute top-[-0.16rem] left-[25.99rem] h-[35.88rem] w-[60.19rem] mix-blend-luminosity">
            <Image
              src={temple}
              alt=""
              fill
              sizes="30vw"
              className="object-cover"
            />
            <div className="absolute inset-0 bg-[linear-gradient(88.78deg,#123129_0.62%,rgba(18,49,41,0)_39.2%)]" />
          </div>
          <DotsTexture />
        </>
      );
    case "floating-cards": {
      const [challenge, evaluation, instant] = FLOATING_CARDS[language] ?? FLOATING_CARDS.en;
      return (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-[0.1rem] h-[47.375rem] rounded-[0.19rem] opacity-10 mix-blend-luminosity">
            <Image
              src={statue}
              alt=""
              fill
              sizes="(min-width: 993px) 30vw, 100vw"
              className="rounded-[0.19rem] object-cover object-bottom"
            />
          </div>
          <Noise className="top-[-1.03rem] left-[-2.25rem]" />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 select-none">
            <FloatingProductCard
              blurred
              className="top-[-4.125rem] left-[28.179rem]"
              title={challenge.title}
              text={challenge.text}
            />
            <FloatingProductCard
              className="top-[9.124rem] left-[24.625rem]"
              title={evaluation.title}
              text={evaluation.text}
            />
            <FloatingProductCard
              blurred
              className="top-[24.166rem] left-[28.179rem]"
              title={instant.title}
              text={instant.text}
            />
          </div>
        </>
      );
    }
    case "horse":
      return (
        <>
          <Noise className="top-[0.025rem] left-0" />
          <div
            aria-hidden
            className="pointer-events-none absolute top-[-1.714rem] left-[7.125rem] h-[56rem] w-[56rem] -scale-x-100 lg:top-[-2.275rem] lg:h-[69.375rem] lg:w-[69.375rem]">
            <Image
              src={horse}
              alt=""
              fill
              sizes="(min-width: 993px) 30vw, 100vw"
              className="object-cover"
            />
          </div>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-[16.35rem] h-[29.125rem] bg-gradient-to-b from-[rgba(18,49,41,0)] to-[#123129]"
          />
          <DotsTexture />
        </>
      );
    case "fresco":
      return (
        <div
          aria-hidden
          className="pointer-events-none absolute top-[-0.305rem] left-[17.36rem] h-[35.69rem] w-[55.5rem] opacity-30 mix-blend-luminosity">
          <Image
            src={fresco}
            alt=""
            fill
            sizes="30vw"
            className="object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-[linear-gradient(92.1deg,#123129_2.38%,rgba(18,49,41,0)_29.48%)]" />
        </div>
      );
    default:
      return null;
  }
}

// ─── Card ────────────────────────────────────────────────────────────────────

function WhyIqCardV2({
  card,
  position,
  language,
}: {
  card: PageHomeWhyIqCardV2;
  position: CardPosition;
  language: string;
}) {
  const tone = (stegaClean(card.tone) ?? "forest") as PageHomeWhyIqCardV2Tone;
  const artwork = (stegaClean(card.artwork) ?? "none") as PageHomeWhyIqCardV2Artwork;

  return (
    <article
      className={cn(
        "relative isolate flex flex-col overflow-hidden p-[4rem] text-white",
        TONE_CLASS[tone] ?? TONE_CLASS.forest,
        position === "side" && "h-[30.5rem] justify-between rounded-[0.25rem] border border-white/10 lg:h-auto lg:flex-1",
        position === "center-top" && "h-[47.5rem] justify-between lg:justify-end lg:gap-[2.875rem]",
        position === "center-bottom" && "h-[45.5rem] justify-end gap-[2.875rem]",
      )}>
      <CardArtwork
        kind={artwork}
        language={language}
      />

      {/* Icon box */}
      <div className="relative z-10 grid h-[7rem] w-[7rem] shrink-0 place-items-center rounded-[0.25rem] border border-white/[0.12]">
        <div className="absolute inset-[0.875rem] rounded-[0.25rem] bg-white/[0.12]" />
        <WhyIqIconV2
          icon={stegaClean(card.icon)}
          className="relative"
        />
      </div>

      {/* Eyebrow + description */}
      <div
        className={cn(
          "relative z-10 flex flex-col gap-[1.75rem] [word-break:break-word]",
          position === "center-top" && "max-w-[22.375rem]",
        )}>
        {card.eyebrow && (
          <p className="mb-0 font-geist text-[2.75rem] leading-[0.926] font-semibold text-white">{card.eyebrow}</p>
        )}
        {card.description && (
          <p className="mb-0 font-geist text-[2.25rem] leading-[1.55] tracking-[-0.0873rem] whitespace-pre-line text-white/70">
            {card.description}
          </p>
        )}
      </div>
    </article>
  );
}

// ─── Section ─────────────────────────────────────────────────────────────────

export default function WhyIqSectionV2({ data }: { data: PageHomeSchema }) {
  const cards = data.whyIqCardsV2 ?? [];
  const language = stegaClean(data.settings?.language) ?? DEFAULT_LANGUAGE;

  if (!data.whyIqTitle && !cards.length) return null;

  // Bento layout by order: 1–3 left column, 4–5 center column (tall), 6–8 right column.
  const columns = [cards.slice(0, 3), cards.slice(3, 5), cards.slice(5, 8)];

  return (
    <section className="w-full bg-white pt-[8rem] lg:pt-[18rem]">
      {/* Header */}
      <div className="mx-auto flex w-[min(86rem,calc(100%-4rem))] flex-col items-center gap-[1.5rem] pb-[4rem] text-center lg:pb-[7.875rem]">
        {data.whyIqChip && (
          <p className="mb-0 font-blacker text-[2rem] leading-[1.5] text-black italic">{data.whyIqChip}</p>
        )}
        {data.whyIqTitle && (
          <div className="richtext flex w-full flex-col gap-[2rem] prose-p:max-w-[60rem] prose-em:text-[#003828]">
            <PortableText
              value={data.whyIqTitle}
              components={richTextComponent}
            />
          </div>
        )}
      </div>

      {/* Cards */}
      {cards.length > 0 && (
        <div className="mx-auto flex w-[min(150rem,calc(100%-4rem))] flex-col gap-[1.25rem] lg:grid lg:grid-cols-[40.25rem_65.5rem_40.25rem] lg:justify-center lg:gap-[2rem]">
          {columns.map((column, columnIndex) => (
            <div
              key={columnIndex}
              className="contents lg:flex lg:h-[95rem] lg:flex-col lg:gap-[2rem]">
              {column.map((card, index) => (
                <WhyIqCardV2
                  key={card._key ?? `${columnIndex}-${index}`}
                  card={card}
                  position={columnIndex !== 1 ? "side" : index === 0 ? "center-top" : "center-bottom"}
                  language={language}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Summary + CTA */}
      {(data.whyIqSummary || data.whyIqButton) && (
        <div className="mx-auto flex w-[min(86rem,calc(100%-4rem))] flex-col items-center gap-[4.25rem] pt-[6rem] pb-[8rem] lg:pt-[8rem] lg:pb-[15rem]">
          {data.whyIqSummary && (
            <p className="mb-0 max-w-[47.75rem] text-center font-geist text-[2rem] leading-[1.33] text-black/70">
              {data.whyIqSummary}
            </p>
          )}
          {data.whyIqButton && <CTAButton data={data.whyIqButton} />}
        </div>
      )}
    </section>
  );
}
